import { CheckCircle2, XCircle, RotateCcw, Clock } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { cn } from '@/lib/utils'

type ExecutionDetailsProps = {
  terminal_reason?: string
  terminal_message?: string
  restart_count?: number
  terminal_exit_code?: number
  terminal_finished_at?: string
  status?: string
}

export const ExecutionDetails = ({
  terminal_reason,
  terminal_message,
  restart_count,
  terminal_exit_code,
  terminal_finished_at,
  status,
}: ExecutionDetailsProps) => {
  if (!terminal_reason) {
    const isRunning = status?.toLowerCase() === 'running'
    if (!isRunning) return null
    return (
      <div className="flex items-center gap-2">
        <div className="w-2 h-2 rounded-full bg-primary animate-pulse" />
        <span className="text-sm font-medium text-primary">Running</span>
      </div>
    )
  }
  const isSuccess = terminal_exit_code === 0

  return (
    <div className="mt-4 space-y-3">
      {/* Status Header */}
      <div className="flex items-center gap-2">
        {isSuccess ? (
          <CheckCircle2 className="w-4 h-4 text-green-500" />
        ) : (
          <XCircle className="w-4 h-4 text-red-500" />
        )}
        <span className="font-medium text-sm">
          {isSuccess ? 'Completed Successfully' : 'Execution Failed'}
        </span>
        <Badge
          variant={isSuccess ? 'secondary' : 'destructive'}
          className="text-xs"
        >
          Exit {terminal_exit_code}
        </Badge>
      </div>

      {/* Details Card */}
      <div
        className={cn(
          'p-4 rounded-lg border',
          isSuccess
            ? 'bg-green-50 border-green-200 dark:bg-green-950/20 dark:border-green-800/30'
            : 'bg-red-50 border-red-200 dark:bg-red-950/20 dark:border-red-800/30'
        )}
      >
        <div className="space-y-1 mb-3">
          <p className="text-sm font-medium text-foreground/90 leading-relaxed">
            {terminal_reason}
          </p>
          <p className="text-xs text-foreground/70">{terminal_message}</p>
        </div>

        <div className="flex flex-wrap gap-4 pt-2">
          {restart_count && restart_count > 0 && (
            <div className="flex items-center gap-1.5">
              <RotateCcw className="w-3.5 h-3.5 text-foreground/60" />
              <span className="text-xs text-foreground/70">
                {restart_count} restart{restart_count > 1 ? 's' : ''}
              </span>
            </div>
          )}
          {terminal_finished_at && (
            <div className="flex items-center gap-1.5">
              <Clock className="w-3.5 h-3.5 text-foreground/60" />
              <span className="text-xs text-foreground/70">
                {new Date(terminal_finished_at).toLocaleDateString()} at{' '}
                {new Date(terminal_finished_at).toLocaleTimeString([], {
                  hour: '2-digit',
                  minute: '2-digit',
                })}
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
